import { observer } from 'mobx-react-lite';
import * as React from 'react';
import CharmSuggestionRow from './CharmSuggestionRow';
import CharmSuggestionsItem from 'owa-calendar-charm-suggestions-store/lib/schema/CharmSuggestionsItem';
import CharmSuggestionsStore from 'owa-calendar-charm-suggestions-store/lib/schema/CharmSuggestionsStore';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import setCharmSuggestions from 'owa-calendar-charm-suggestions-store/lib/actions/setCharmSuggestions';
import { Callout, DirectionalHint } from '@fluentui/react/lib/Callout';
import {
    CHARMS_USAGE_SUGGESTIONS_SHOWN,
    charmsDatapoints,
    getAllCharmKeywords,
    getCharmForIdFromStore,
    getKeywordInfo,
} from 'owa-calendar-charms-store';
import { dismissCharmSuggestions } from 'owa-calendar-charm-suggestions-store/lib/actions/charmSuggestionActions';
import { logUsage } from 'owa-analytics';

let styles = require('./CharmSuggestions.scss');

const MAX_SUGGESTIONS = 3;

export interface CharmSuggestionsProps extends React.HTMLProps<HTMLDivElement> {
    subject: string;
    state: CharmSuggestionsStore;
    onSelectIcon: (charmSuggestionsItem: CharmSuggestionsItem) => void;
    containerClass: string;
    target: any;
    colorScheme?: ColorScheme;
}

export default observer(function CharmSuggestions(props: CharmSuggestionsProps) {
    /**
     * Create a list of up to 3 JSX elements as suggestions from the first word.
     * Check if the start of that word matches any keywords, if yes then add up to 3 keywords to the return elements list
     */
    const createSuggestions = () => {
        let suggestions: CharmSuggestionsItem[] = [];
        let { state, colorScheme, onSelectIcon } = props;
        let retElements: JSX.Element[] = [];
        let highlightedItem: CharmSuggestionsItem = null;
        let words = props.subject ? props.subject.split(' ') : [' '];
        //search the keywords for the one that starts with the charm
        let keywords = getAllCharmKeywords();
        let word = words[0].toLocaleLowerCase();
        let indexOfCurrentKeyword = 0; // iterate through keywords
        let indexOfSuggestion = 0; // build elements list
        let addedCharms: number[] = [];
        while (
            indexOfCurrentKeyword <= keywords.length &&
            indexOfSuggestion < MAX_SUGGESTIONS &&
            word != ' '
        ) {
            let currentKeywordFromFullList = keywords[indexOfCurrentKeyword];
            indexOfCurrentKeyword++;
            if (
                currentKeywordFromFullList &&
                currentKeywordFromFullList.substring(0, word.length).toLocaleLowerCase() == word
            ) {
                let keywordInfo = getKeywordInfo(currentKeywordFromFullList);
                if (keywordInfo == null) {
                    continue;
                }
                let charm = getCharmForIdFromStore(keywordInfo.IconId);
                if (charm != null && !addedCharms.includes(charm.IconId)) {
                    addedCharms.push(charm.IconId);
                    let charmSuggestionsItem = { charm: charm, keyword: keywordInfo.Keyword };
                    let isHighlighed: boolean = indexOfSuggestion == state.highlightedIndex;
                    if (isHighlighed) {
                        highlightedItem = charmSuggestionsItem;
                    }
                    suggestions.push(charmSuggestionsItem);
                    retElements.push(
                        <CharmSuggestionRow
                            key={`${charm.IconId}-${indexOfSuggestion}`}
                            colorScheme={colorScheme}
                            isHighlighted={isHighlighed}
                            onSelectIcon={onSelectIcon}
                            charmSuggestionsItem={charmSuggestionsItem}
                        />
                    );
                    indexOfSuggestion++;
                }
            }
        }
        setCharmSuggestions(suggestions);
        // Hide the suggestions if there aren't any results
        if (suggestions.length == 0) {
            dismissCharmSuggestions();
        }
        if (state.shouldShowSuggestions == true && highlightedItem != null) {
            retElements.push(createAriaLiveDiv(highlightedItem));
        }
        // Log generated suggestions.
        if (state.shouldShowSuggestions == true) {
            suggestions.forEach(suggestion => {
                const { customData } = charmsDatapoints.CharmsDatapoint.suggestionsshowndatapoint;
                logUsage(
                    CHARMS_USAGE_SUGGESTIONS_SHOWN,
                    customData(suggestion.charm.IconId, suggestion.keyword)
                );
            });
        }
        return retElements;
    };
    if (props.state.shouldShowSuggestions) {
        return (
            <Callout
                className={props.containerClass}
                isBeakVisible={false}
                target={props.target}
                directionalHint={DirectionalHint.bottomLeftEdge}
                onDismiss={dismissCharmSuggestions}>
                <div key={'charmSuggestions'} className={styles.suggestionsContainer}>
                    {createSuggestions()}
                </div>
            </Callout>
        );
    }
    return null;
});

/**
 * Make the div for the Aria Live region needed for narrating the charm suggestion items.
 */
function createAriaLiveDiv(charmSuggestionsItem: CharmSuggestionsItem) {
    return (
        <div
            className={styles.narratorOnly}
            role="row"
            aria-live="polite"
            aria-atomic="true"
            key="selectedSuggestion">
            {charmSuggestionsItem.charm.LocalizedName}
            {charmSuggestionsItem.keyword}
        </div>
    );
}
